import * as React from "react";
import { useCallback, useMemo, useState } from "react";
import { FormattedNumber } from "react-intl";
import { Link } from "react-router-dom";
import type { Receipt as ApiReceipt } from "randmar-api-client";
import { useGetV4WarehousesQuery } from "randmar-api-client";
import { Info } from "lucide-react";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Card,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "randmarcomps";
import { useManufacturer } from "../../contexts/WithManufacturer";
import Path from "../../routes/Path";
import type { Receipt as LegacyReceipt } from "../../stores/document/Document";
import CurrencyUtils from "../util/CurrencyUtils";
import FormatDateFromInt from "../util/DateTimeUtil";
import { TablePaginationBar } from "../util/TablePaginationBar";

interface IProps {
  receipts?: Array<LegacyReceipt | ApiReceipt>;
  defaultRowsPerPage?: number;
}

interface ReceiptSummaryRow {
  receiptNumber: string;
  purchaseOrderNumber?: string;
  location?: string | null;
  receiptDate?: number;
  invoiced?: boolean;
  invoiceNumber?: string;
  comments?: string;
  totalValue: number;
}

const rowsPerPageOptions = [10, 20, 50, 100] as const;

const isLegacyReceipt = (receipt: LegacyReceipt | ApiReceipt): receipt is LegacyReceipt =>
  "receiptNumber" in receipt;

const ReceiptsGrid: React.FC<IProps> = ({ receipts, defaultRowsPerPage = 20 }) => {
  const { applicationId } = useManufacturer();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
  const { data: warehouses } = useGetV4WarehousesQuery();

  const getWarehouseNameFromCode = useCallback((code: string | undefined | null): string => {
    if (!code || !warehouses) {
      return "B/O";
    }
    const warehouse = warehouses.find(w => w.WarehouseId === code);
    return warehouse?.Name || "B/O";
  }, [warehouses]);

  const summarizedRows = useMemo<ReceiptSummaryRow[]>(() => {
    const rows = new Map<string, ReceiptSummaryRow>();

    for (const receipt of receipts ?? []) {
      const receiptNumber = isLegacyReceipt(receipt)
        ? receipt.receiptNumber || ""
        : receipt.ReceiptNumber || "";
      if (!receiptNumber) {
        continue;
      }

      const receiptDate = isLegacyReceipt(receipt) ? receipt.receiptDate : receipt.ReceiptDate;
      const location = isLegacyReceipt(receipt) ? receipt.location : receipt.Location;

      const totalValue = isLegacyReceipt(receipt)
        ? receipt.subTotal || 0
        : (receipt.Quantity ?? 0) * (receipt.UnitPrice ?? 0);

      const existing = rows.get(receiptNumber);
      if (!existing) {
        rows.set(receiptNumber, {
          receiptNumber,
          purchaseOrderNumber: isLegacyReceipt(receipt) ? receipt.purchaseOrderNumber : undefined,
          location,
          receiptDate,
          invoiced: isLegacyReceipt(receipt) ? receipt.invoiced : undefined,
          invoiceNumber: isLegacyReceipt(receipt) ? receipt.invoiceNumber : undefined,
          comments: isLegacyReceipt(receipt) ? receipt.comments : undefined,
          totalValue,
        });
        continue;
      }

      existing.totalValue += totalValue;
      existing.location = existing.location || location;
      existing.receiptDate = Math.max(existing.receiptDate ?? 0, receiptDate ?? 0) || undefined;
    }

    return Array.from(rows.values()).sort((a, b) => b.receiptNumber.localeCompare(a.receiptNumber));
  }, [receipts]);

  if (!summarizedRows.length) {
    return (
      <Alert className="w-full rounded-2xl border border-gray-100 bg-white shadow-sm">
        <Info className="h-4 w-4" aria-hidden="true" />
        <AlertTitle>Receipts</AlertTitle>
        <AlertDescription>
          Receipts document inventory received into Randmar warehouses. There are no receipts to display right now.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Card className="w-full rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h3 className="text-[20px] font-medium text-slate-900">Receipts</h3>
          <p className="text-[15px] text-slate-600">
            Totals are aggregated across receipt line items.
          </p>
        </div>
        <p className="text-sm text-slate-500">
          {summarizedRows.length} receipt{summarizedRows.length === 1 ? "" : "s"}
        </p>
      </div>

      <div className="mt-4 overflow-x-auto rounded-2xl border border-gray-100">
        <TooltipProvider>
          <Table>
            <TableHeader className="bg-gray-50">
              <TableRow className="h-14 hover:bg-gray-50">
                <TableHead className="w-[130px] text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Status
                </TableHead>
                <TableHead className="min-w-[170px] text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Receipt
                </TableHead>
                <TableHead className="min-w-[170px] text-xs font-semibold uppercase tracking-wide text-slate-500">
                  PO number
                </TableHead>
                <TableHead className="min-w-[180px] text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Location
                </TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Date
                </TableHead>
                <TableHead className="text-right text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Value
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {summarizedRows
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row) => {
                  const statusVariant =
                    row.invoiced === true ? "success" : row.invoiced === false ? "warning" : "info";
                  const statusLabel =
                    row.invoiced === true ? "Invoiced" : row.invoiced === false ? "Uninvoiced" : "Unknown";

                  const tooltipLines = [
                    row.comments ? `Comments: ${row.comments}` : null,
                    row.invoiceNumber ? `Invoice #: ${row.invoiceNumber}` : null,
                  ].filter(Boolean);

                  return (
                    <TableRow
                      key={row.receiptNumber}
                      className="h-14 border-b border-gray-100 hover:bg-gray-50/60"
                    >
                      <TableCell>
                        {tooltipLines.length ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div>
                                <Badge variant={statusVariant} size="sm">
                                  {statusLabel}
                                </Badge>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs text-sm">
                              {tooltipLines.map((line) => (
                                <p key={line}>{line}</p>
                              ))}
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          <Badge variant={statusVariant} size="sm">
                            {statusLabel}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="font-semibold">
                        <Link
                          to={Path.document(applicationId, row.receiptNumber)}
                          className="text-slate-900 underline-offset-4 hover:text-[color:var(--ops-accent)] hover:underline"
                        >
                          {row.receiptNumber}
                        </Link>
                      </TableCell>
                      <TableCell className="text-slate-700">{row.purchaseOrderNumber || "—"}</TableCell>
                      <TableCell className="text-slate-700">
                        {getWarehouseNameFromCode(row.location)}
                      </TableCell>
                      <TableCell className="text-slate-700">{FormatDateFromInt(row.receiptDate)}</TableCell>
                      <TableCell className="text-right font-medium text-slate-900">
                        <FormattedNumber {...CurrencyUtils.format} value={row.totalValue} />
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TooltipProvider>
      </div>

      {summarizedRows.length > rowsPerPage && (
        <TablePaginationBar
          page={page}
          rowsPerPage={rowsPerPage}
          totalRows={summarizedRows.length}
          rowsPerPageOptions={rowsPerPageOptions}
          onPageChange={(nextPage) => setPage(nextPage)}
          onRowsPerPageChange={(nextRows) => setRowsPerPage(nextRows)}
        />
      )}
    </Card>
  );
};

export default ReceiptsGrid;
